import { Component, h, Prop, State, Listen, Watch, Event, Method, Host, Element } from '@stencil/core';
import { generateRandomNumber, getInheritedBackgroundColor, hideFocusRingWhenUsingMouse } from '../../../global/utils/utils';
/** Known issues TODO
 *  Screen reader incorrectly reads out the details of the input box again when the manual address modal is opened
 *  Once the modal is opened shift tab ( ie reverse order) will miss the footer buttons
 **/
export class Autocomplete {
  constructor() {
    /**
     * The autocomplete ID
     */
    this.autoCompleteId = generateRandomNumber();
    /**
     * The label text for the input, will be hidden if type is experiential
     */
    this.label = '';
    /**
     * Flag to show error state
     */
    this.hasError = false;
    /**
     * Text to show when valid. Will override the hint text if present at the bottom of the component.
     */
    this.validText = '';
    /**
     * Shows the valid icon when validSelectionMode is set to external or onSelect and showValidIcon
     * equals true. validSelectionMode should be used when an api call regulates the visibility of the
     * icon ie when an address is searched for by the user and the address is serviceable. onSelect should
     * be used with a static list of options (eg street Name) where any selection is valid
     * If set to false, valid state will never display.
     */
    this.validSelectionMode = 'off';
    /**
     * Type for the control, either default, default-inverse or experiential
     */
    this.type = 'default';
    /**
     * The dropdown direction. Set to up when the autocomplete is at the bottom of the page.
     */
    this.dropdownDirection = 'down';
    /**
     * How many characters to type before the autocomplete starts triggering
     */
    this.minCharactersToSearch = 1;
    /**
     * Mode for the autocomplete uncontrolled will internally filter the options for the autocomplete, controlled will let the consumer pass in the current state and results to display
     */
    this.mode = 'uncontrolled';
    this.filteredResults = [];
    this.currentState = 'idle';
    this.selectedIndex = -1;
    this.dropdownOptions = [];
    this.hasSelection = false;
    this.optionElements = [];
  }
  valueWatchHandler(newValue) {
    this.inputValue = newValue;
  }
  optionsStringWatchHandler(newValue) {
    this.dropdownOptions = JSON.parse(newValue);
    if (this.mode === 'controlled') {
      this.filteredResults = this.dropdownOptions;
    }
  }
  optionsWatchHandler(newValue) {
    this.dropdownOptions = newValue;
    if (this.mode === 'controlled') {
      this.filteredResults = this.dropdownOptions;
    }
  }
  parentStateWatchHandler(newValue) {
    this.currentState = newValue;
  }
  currentStateWatchHandler(newValue) {
    if (newValue === 'displayResults') {
      this.setInputElementAttribute('aria-expanded', 'true');
      hideFocusRingWhenUsingMouse(this.ul);
    }
    else {
      this.setInputElementAttribute('aria-expanded', 'false');
      this.setInputElementAttribute('aria-activedescendant', '');
    }
  }
  /**
   * Gets the textbox component element
   */
  async getTextBoxElement() {
    return this.textboxComponent;
  }
  /**
   * Sets the textbox component element
   */
  async setTextBoxValue(textValue) {
    await this.textboxComponent.setInput(textValue);
  }
  onScroll(event) {
    if (this.currentState === 'displayResults' && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
    }
  }
  textboxInputHandler(event) {
    const eventTarget = event.detail.target;
    const value = eventTarget.value;
    if (this.mode === 'uncontrolled') {
      this.filteredResults = this.dropdownOptions.filter((x) => value.length >= this.minCharactersToSearch && x.text.toLowerCase().startsWith(value.toLowerCase()));
      this.currentState = 'displayResults';
    }
    this.autocompleteInput.emit(event.detail);
  }
  textboxFocusHandler() {
    this.selectedIndex = 0;
    this.inputElement.setAttribute('aria-activedescendant', '');
    if (this.filteredResults && this.filteredResults.length && this.currentState !== 'resultSelected') {
      this.currentState = 'displayResults';
    }
  }
  textboxBlurHandler(event) {
    if (this.currentState === 'loading') {
      return;
    }
    const nextTarget = event.relatedTarget || document.activeElement;
    if (!nextTarget ||
      (this.optionElements.indexOf(nextTarget) === -1 &&
        this.optionElements.indexOf(nextTarget.parentElement) === -1)) {
      this.currentState = this.currentState === 'resultSelected' ? 'resultSelected' : 'idle';
      this.autocompleteBlur.emit(event);
    }
  }
  textboxKeydownHandler(event) {
    this.hasSelection = false;
    if (event.detail.key === 'ArrowDown' || event.detail.key === 'Down') {
      const validListItems = this.optionElements.filter((x) => !!x);
      if (validListItems.length) {
        const firstOption = this.optionElements.find((o) => o.id === `${this.autoCompleteId}__option-0`);
        if (firstOption) {
          firstOption.focus();
          this.selectedIndex = 0;
          this.setInputElementAttribute('aria-activedescendant', `${this.autoCompleteId}__option-0`);
        }
      }
    }
    else if (event.detail.key === 'Escape' || event.detail.key === 'Esc') {
      this.currentState = 'idle';
    }
  }
  isValidDisplayHelper(selectionMode, showIcon) {
    if (showIcon) {
      if (selectionMode === 'onSelect' && this.hasSelection) {
        return true;
      }
      if (selectionMode === 'external') {
        return true;
      }
    }
    return false;
  }
  setInputElementAttribute(attributeName, value) {
    if (this.inputElement) {
      this.inputElement.setAttribute(attributeName, value);
    }
  }
  focusOnNextOption() {
    const validListItems = this.optionElements.filter((x) => !!x);
    if (this.selectedIndex !== validListItems.length - 1) {
      this.selectedIndex++;
      validListItems[this.selectedIndex].focus();
      this.setInputElementAttribute('aria-activedescendant', `${this.autoCompleteId}__option-${this.selectedIndex}`);
    }
  }
  focusOnPreviousOption() {
    const validListItems = this.optionElements.filter((x) => !!x);
    if (this.selectedIndex === 0) {
      this.inputElement.focus();
    }
    else {
      this.selectedIndex--;
      validListItems[this.selectedIndex].focus();
      this.setInputElementAttribute('aria-activedescendant', `${this.autoCompleteId}__option-${this.selectedIndex}`);
    }
  }
  onOptionSelected(x) {
    this.hasSelection = true;
    if (!x.doNotComplete) {
      this.inputValue = x.text;
    }
    this.inputElement.focus();
    this.currentState = 'resultSelected';
    this.autocompleteOptionSelected.emit(x);
  }
  onOptionKeyDown(e) {
    switch (e.key) {
      case 'Down':
      case 'ArrowDown':
        this.focusOnNextOption();
        break;
      case 'Up':
      case 'ArrowUp':
        this.focusOnPreviousOption();
        break;
      case 'Esc':
      case 'Escape':
        this.currentState = 'idle';
        break;
      case 'Enter':
      case ' ':
        e.target.click();
        break;
      default:
        break;
    }
  }
  onOptionBlur(event) {
    if (this.currentState === 'loading') {
      return;
    }
    const nextTarget = event.relatedTarget || document.activeElement;
    if (!nextTarget ||
      (this.optionElements.indexOf(nextTarget) === -1 &&
        this.optionElements.indexOf(nextTarget.parentElement) === -1)) {
      this.currentState = this.currentState === 'resultSelected' ? 'resultSelected' : 'idle';
      this.autocompleteBlur.emit(event);
    }
  }
  componentWillLoad() {
    this.dropdownOptions = this.optionsString ? JSON.parse(this.optionsString) : this.options;
    this.currentState = this.parentState ? this.parentState : this.currentState;
    if (this.mode === 'controlled') {
      this.filteredResults = this.dropdownOptions;
    }
    this.inputValue = this.value;
  }
  componentWillRender() {
    this.host.style.setProperty('--autocomplete-background-color', getInheritedBackgroundColor(this.host));
  }
  componentDidRender() {
    this.optionElements = Array.from(this.host.querySelectorAll('li'));
  }
  async componentDidLoad() {
    this.inputElement = await this.textboxComponent.getInputElement();
    this.setInputElementAttribute('aria-expanded', 'false');
    this.setInputElementAttribute('aria-owns', `${this.autoCompleteId}_list`);
    this.setInputElementAttribute('aria-autocomplete', 'list');
    this.setInputElementAttribute('role', 'combobox');
  }
  renderHighlightedText(option) {
    const index = option.text.indexOf(option.highlightText);
    const firstSection = option.text.substr(0, index);
    const highlight = option.highlightText;
    const lastSection = option.text.substring(index + option.highlightText.length, option.text.length);
    return [
      h("span", { class: "agl-ds-autocomplete__dropdown-item-text--no-overflow" }, firstSection),
      h("agl-ds-text", { appearance: "highlight", fontWeight: "semibold" }, highlight),
      h("span", { class: "agl-ds-autocomplete__dropdown-item-text" }, lastSection)
    ];
  }
  render() {
    this.optionElements.splice(0, this.optionElements.length);
    return (h(Host, null,
      h("div", { class: "agl-ds-autocomplete", ref: (el) => (this.ul = el) },
        h("agl-ds-textbox", { textBoxId: `${this.autoCompleteId}_textBox`, label: this.label, value: this.inputValue, hasError: this.hasError, type: this.type, maxLength: this.maxLength, placeholder: this.placeholder, hintText: this.hintText, isInputValid: this.isValidDisplayHelper(this.validSelectionMode, this.showValidIcon), validationText: this.validationText, validText: this.validText, browserAutocomplete: false, onTextboxBlur: (e) => this.textboxBlurHandler(e.detail), ref: (el) => (this.textboxComponent = el), "aria-autocomplete": "list" }),
        this.currentState === 'loading' && [
          h("span", { class: {
              'loading-overlay': true,
              ['loading-overlay--inverse']: this.type === 'default-inverse',
              ['loading-overlay--experiential']: this.type === 'experiential'
            } }),
          h("agl-ds-loading-indicator", { size: "sm", class: {
              ['loading-indicator']: true,
              ['loading-indicator--experiential']: this.type === 'experiential'
            } })
        ],
        this.currentState === 'displayResults' && (h("ul", { class: {
            ['agl-ds-autocomplete__dropdown']: true,
            ['agl-ds-autocomplete__dropdown--experiential']: this.type === 'experiential',
            ['agl-ds-autocomplete__dropdown--up']: this.dropdownDirection === 'up'
          }, role: "listbox", id: `${this.autoCompleteId}_list` }, this.filteredResults.map((x, index) => (h("li", { key: x.text, id: `${this.autoCompleteId}__option-${index}`, tabIndex: -1, class: "agl-ds-autocomplete__dropdown-item", role: "option", onClick: () => this.onOptionSelected(x), onKeyDown: (e) => this.onOptionKeyDown(e), onBlur: (e) => this.onOptionBlur(e), "aria-selected": `${this.selectedIndex === index}`, "aria-posinset": index + 1, "aria-setsize": this.filteredResults.length }, !x.highlightText ? h("span", { class: "agl-ds-autocomplete__dropdown-item-text" }, x.text) : this.renderHighlightedText(x)))))))));
  }
  static get is() { return "agl-ds-autocomplete"; }
  static get originalStyleUrls() { return {
    "$": ["./autocomplete.component.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["autocomplete.component.css"]
  }; }
  static get properties() { return {
    "autoCompleteId": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The autocomplete ID"
      },
      "attribute": "auto-complete-id",
      "reflect": false,
      "defaultValue": "generateRandomNumber()"
    },
    "label": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The label text for the input, will be hidden if type is experiential"
      },
      "attribute": "label",
      "reflect": false,
      "defaultValue": "''"
    },
    "value": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The value for the input"
      },
      "attribute": "value",
      "reflect": false
    },
    "placeholder": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The placeholder property for the input, only used if type is experiential"
      },
      "attribute": "placeholder",
      "reflect": false
    },
    "maxLength": {
      "type": "number",
      "mutable": false,
      "complexType": {
        "original": "number",
        "resolved": "number",
        "references": {}
      },
      "required": false,
      "optional": true,
      "docs": {
        "tags": [],
        "text": "The maxlength property of the input: note max length does nothing when inputType is number as it technically not valid [link to mozilla docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input#attr-maxlength)"
      },
      "attribute": "max-length",
      "reflect": false
    },
    "hasError": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Flag to show error state"
      },
      "attribute": "has-error",
      "reflect": false,
      "defaultValue": "false"
    },
    "validationText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Validation text to show when the has error flag is true"
      },
      "attribute": "validation-text",
      "reflect": false
    },
    "validText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Text to show when valid. Will override the hint text if present at the bottom of the component."
      },
      "attribute": "valid-text",
      "reflect": false,
      "defaultValue": "''"
    },
    "showValidIcon": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Shows the valid icon when it equals true and validSelectionMode is set to external or onSelect"
      },
      "attribute": "show-valid-icon",
      "reflect": false
    },
    "validSelectionMode": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "validSelectionMode",
        "resolved": "\"external\" | \"off\" | \"onSelect\"",
        "references": {
          "validSelectionMode": {
            "location": "import",
            "path": "./autocomplete.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Shows the valid icon when validSelectionMode is set to external or onSelect and showValidIcon\nequals true. validSelectionMode should be used when an api call regulates the visibility of the\nicon ie when an address is searched for by the user and the address is serviceable. onSelect should\nbe used with a static list of options (eg street Name) where any selection is valid\nIf set to false, valid state will never display."
      },
      "attribute": "valid-selection-mode",
      "reflect": false,
      "defaultValue": "'off'"
    },
    "hintText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Hint text will be shown underneath the textbox but will be hidden if there is an error"
      },
      "attribute": "hint-text",
      "reflect": false
    },
    "type": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "TextboxType",
        "resolved": "\"default\" | \"default-inverse\" | \"experiential\"",
        "references": {
          "TextboxType": {
            "location": "import",
            "path": "../../core/textbox/textbox.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Type for the control, either default, default-inverse or experiential"
      },
      "attribute": "type",
      "reflect": false,
      "defaultValue": "'default'"
    },
    "dropdownDirection": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The dropdown direction. Set to up when the autocomplete is at the bottom of the page."
      },
      "attribute": "dropdown-direction",
      "reflect": false,
      "defaultValue": "'down'"
    },
    "minCharactersToSearch": {
      "type": "number",
      "mutable": false,
      "complexType": {
        "original": "number",
        "resolved": "number",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "How many characters to type before the autocomplete starts triggering"
      },
      "attribute": "min-characters-to-search",
      "reflect": false,
      "defaultValue": "1"
    },
    "mode": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "AutocompleteMode",
        "resolved": "\"controlled\" | \"uncontrolled\"",
        "references": {
          "AutocompleteMode": {
            "location": "import",
            "path": "./autocomplete.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Mode for the autocomplete uncontrolled will internally filter the options for the autocomplete, controlled will let the consumer pass in the current state and results to display"
      },
      "attribute": "mode",
      "reflect": false,
      "defaultValue": "'uncontrolled'"
    },
    "parentState": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "AutocompleteState",
        "resolved": "\"displayResults\" | \"idle\" | \"loading\" | \"resultSelected\"",
        "references": {
          "AutocompleteState": {
            "location": "import",
            "path": "./autocomplete.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets the state of the autocomplete in controlled mode"
      },
      "attribute": "parent-state",
      "reflect": false
    },
    "options": {
      "type": "unknown",
      "mutable": false,
      "complexType": {
        "original": "AutoCompleteOption[]",
        "resolved": "AutoCompleteOption[]",
        "references": {
          "AutoCompleteOption": {
            "location": "import",
            "path": "./autocompleteResult"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Autocomplete options which can be passed in using TSX or JS e.g. document.getElementByTagName('agl-ds-autocomplete').options = []\nUse either this or optionsString"
      }
    },
    "optionsString": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Autocomplete options which can be passed in using a JSON string"
      },
      "attribute": "options-string",
      "reflect": false
    }
  }; }
  static get states() { return {
    "filteredResults": {},
    "currentState": {},
    "inputValue": {},
    "selectedIndex": {},
    "dropdownOptions": {},
    "hasSelection": {}
  }; }
  static get events() { return [{
      "method": "autocompleteOptionSelected",
      "name": "autocompleteOptionSelected",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when an option is selected from the dropdown"
      },
      "complexType": {
        "original": "AutoCompleteOption",
        "resolved": "AutoCompleteOption",
        "references": {
          "AutoCompleteOption": {
            "location": "import",
            "path": "./autocompleteResult"
          }
        }
      }
    }, {
      "method": "autocompleteBlur",
      "name": "autocompleteBlur",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when a blur event happens on either the textbox or the dropdown and the next target is not either the textbox or the dropdown"
      },
      "complexType": {
        "original": "FocusEvent",
        "resolved": "FocusEvent",
        "references": {
          "FocusEvent": {
            "location": "global"
          }
        }
      }
    }, {
      "method": "autocompleteInput",
      "name": "autocompleteInput",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Forwards the input event of the textbox input"
      },
      "complexType": {
        "original": "InputEvent",
        "resolved": "InputEvent",
        "references": {
          "InputEvent": {
            "location": "global"
          }
        }
      }
    }]; }
  static get methods() { return {
    "getTextBoxElement": {
      "complexType": {
        "signature": "() => Promise<HTMLAglDsTextboxElement>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          },
          "HTMLAglDsTextboxElement": {
            "location": "global"
          }
        },
        "return": "Promise<HTMLAglDsTextboxElement>"
      },
      "docs": {
        "text": "Gets the textbox component element",
        "tags": []
      }
    },
    "setTextBoxValue": {
      "complexType": {
        "signature": "(textValue: string) => Promise<void>",
        "parameters": [{
            "tags": [],
            "text": ""
          }],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Sets the textbox component element",
        "tags": []
      }
    }
  }; }
  static get elementRef() { return "host"; }
  static get watchers() { return [{
      "propName": "value",
      "methodName": "valueWatchHandler"
    }, {
      "propName": "optionsString",
      "methodName": "optionsStringWatchHandler"
    }, {
      "propName": "options",
      "methodName": "optionsWatchHandler"
    }, {
      "propName": "parentState",
      "methodName": "parentStateWatchHandler"
    }, {
      "propName": "currentState",
      "methodName": "currentStateWatchHandler"
    }]; }
  static get listeners() { return [{
      "name": "keydown",
      "method": "onScroll",
      "target": undefined,
      "capture": true,
      "passive": false
    }, {
      "name": "textboxInput",
      "method": "textboxInputHandler",
      "target": undefined,
      "capture": false,
      "passive": false
    }, {
      "name": "textboxFocus",
      "method": "textboxFocusHandler",
      "target": undefined,
      "capture": false,
      "passive": false
    }, {
      "name": "textboxKeydown",
      "method": "textboxKeydownHandler",
      "target": undefined,
      "capture": false,
      "passive": false
    }]; }
}
